import { useState, useEffect, useCallback } from 'react';
import {
  PAYTMENT_STATUS,
  FUND_PLATFORMS,
  AlgoversePaymentSuccessResponse,
  AlgoversePaymentErrorResponse,
  AlgoversePaymentModalProps,
  UseAlgoversePaymentReturnType,
} from '../types.type';
import {
  fetchAlgoverseFundsMargin,
  createAlgoversePayment,
  triggerAlgoversePaymentStatusSync,
} from 'v3/common/api/algoversePayment';
import { reactToastify, TOASTIFY_TYPES } from 'v3/common/utils/reactToastify';
import subscribeChannel from 'common/api/subscribeChannel';
import { FundsMarginErrorResponse, FundsMarginSuccessResponse } from '../../type';

export const useAlgoversePayment = ({
  orderId,
  selectedPlan,
  showModal,
  setShowModal,
}: AlgoversePaymentModalProps): UseAlgoversePaymentReturnType => {
  const [paymentStatus, setPaymentStatus] = useState<PAYTMENT_STATUS>(PAYTMENT_STATUS.INITIALIZED);
  const [marginResponse, setMarginResponse] = useState<
    FundsMarginSuccessResponse | FundsMarginErrorResponse
  >();
  const [algoversePaymentResponse, setAlgoversePaymentResponse] = useState<
    AlgoversePaymentSuccessResponse | AlgoversePaymentErrorResponse
  >();
  const [selectedFoundPlatform, setSelectedFoundPlatform] = useState<FUND_PLATFORMS>(FUND_PLATFORMS.WEB);
  const [countdown, setCountdown] = useState<{ success: number; pending: number }>({
    success: 3,
    pending: 30,
  });

  const handlePayment = async () => {
    try {
      const response = await createAlgoversePayment({
        reference_id: orderId,
        amount: selectedPlan?.price,
        description: 'Algoverse Payment',
      });

      setPaymentStatus(PAYTMENT_STATUS.PENDING);
      setCountdown((prev) => ({ ...prev, pending: 30 }));

      if (response.status === 'error') {
        reactToastify(`Payment API ${response.errors[0].message}`, TOASTIFY_TYPES.ERROR);
        // setPaymentStatus(PAYTMENT_STATUS.FAILED);
      }
    } catch {
      reactToastify('Something went wrong! While Payment', TOASTIFY_TYPES.ERROR);
      // setPaymentStatus(PAYTMENT_STATUS.FAILED);
    }
  };

  const handleFundsMargin = useCallback(async () => {
    try {
      const response = await fetchAlgoverseFundsMargin();
      setMarginResponse(response);

      const available = response.status === 'success' ? response.data.equity.available_margin : 0;

      if (available < (selectedPlan?.price || 0)) {
        setPaymentStatus(PAYTMENT_STATUS.INSUFFICIENT_FUNDS);
      }

      if (response.status === 'error') {
        reactToastify(`Margin API ${response.errors[0].message}`, TOASTIFY_TYPES.ERROR);
      }
    } catch (e) {
      console.log('funds_margin_error', e);
      reactToastify('Something went wrong! While Fetch Founds Margin', TOASTIFY_TYPES.ERROR);
    }
  }, [selectedPlan?.price]);

  const checkPaymentStatusManually = useCallback(async () => {
    // Trigger status check API; payment status will be updated via WebSocket
    triggerAlgoversePaymentStatusSync(orderId).catch(() => {
      setPaymentStatus(PAYTMENT_STATUS.FAILED);
    });
  }, [orderId]);

  const handlePaymentStatusUpdate = (
    response: AlgoversePaymentSuccessResponse | AlgoversePaymentErrorResponse
  ) => {
    setAlgoversePaymentResponse(response);

    if (response.status === 'success' && response.data.status === 'SUCCESS') {
      setPaymentStatus(PAYTMENT_STATUS.SUCCESS);
    } else {
      setPaymentStatus(PAYTMENT_STATUS.FAILED);
    }
  };

  useEffect(() => {
    if (paymentStatus === PAYTMENT_STATUS.INITIALIZED) handleFundsMargin();
  }, [paymentStatus, handleFundsMargin]);

  useEffect(() => {
    subscribeChannel({
      roomId: `payment_status:${orderId}_order`,
      channelName: 'Algoverse Payment',
      onDataReceive: handlePaymentStatusUpdate,
    });
  }, [orderId]);

  // Reset success countdown when status changes to SUCCESS
  useEffect(() => {
    if (paymentStatus === PAYTMENT_STATUS.SUCCESS) {
      setCountdown((prev) => ({ ...prev, success: 3 }));
    }
  }, [paymentStatus]);

  // Pending countdown: ticks down, calls API at 0
  useEffect(() => {
    if (paymentStatus === PAYTMENT_STATUS.PENDING && countdown.pending > 0) {
      const timer = setTimeout(() => {
        setCountdown((prev) => ({ ...prev, pending: prev.pending - 1 }));
      }, 1000);

      return () => clearTimeout(timer);
    }

    if (paymentStatus === PAYTMENT_STATUS.PENDING && countdown.pending === 0) {
      checkPaymentStatusManually();
    }
  }, [paymentStatus, countdown.pending, checkPaymentStatusManually]);

  // Success countdown: ticks down, redirects at 0
  useEffect(() => {
    if (paymentStatus === PAYTMENT_STATUS.SUCCESS && countdown.success > 0) {
      const timer = setTimeout(() => {
        setCountdown((prev) => ({ ...prev, success: prev.success - 1 }));
      }, 1000);

      return () => clearTimeout(timer);
    }

    if (paymentStatus === PAYTMENT_STATUS.SUCCESS && countdown.success === 0) {
      window.location.href = '/dashboard';
    }
  }, [paymentStatus, countdown.success]);

  return {
    paymentStatus,
    setPaymentStatus,
    marginResponse,
    algoversePaymentResponse,
    selectedFoundPlatform,
    setSelectedFoundPlatform,
    handlePayment,
    showModal,
    setShowModal,
    countdown,
    orderId,
  };
};
